import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {
    AbstractControl,
    FormControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from "@angular/forms";
import {MatPaginator, MatPaginatorModule} from "@angular/material/paginator";
import {MatSortModule} from "@angular/material/sort";
import {catchError, finalize, forkJoin, of, Subject, takeUntil} from "rxjs";
import {fuseAnimations} from "@fuse/animations";
import {MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {CommonModule, DatePipe, NgClass, NgForOf, NgIf} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatRippleModule} from '@angular/material/core';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatDatepickerInputEvent, MatDatepickerModule} from '@angular/material/datepicker';
import {DestinationSmallType, SpecialOffersType, TimeDetails} from '../destinations.types';
import {FuseConfirmationService} from '../../../../../@fuse/services/confirmation';
import {ActivatedRoute} from '@angular/router';
import {NgxSpinnerComponent, NgxSpinnerService} from 'ngx-spinner';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RestFactoryService} from '../../../../core/services/rest-factory.service';
import {Languages} from '../../../../core/types/common.types';
import {MatCardModule} from "@angular/material/card";
import {MatTabsModule} from "@angular/material/tabs";
import {MatChipsModule} from "@angular/material/chips";
import {MatDividerModule} from "@angular/material/divider";

@Component({
    selector: "special-offers-list",
    templateUrl: "./special-offers-list.component.html",
    styleUrls: ["./special-offers-list.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatRippleModule,
        MatSortModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatToolbarModule,
        MatDialogModule,
        MatDatepickerModule,
        NgIf,
        ReactiveFormsModule,
        NgClass,
        NgForOf,
        NgxSpinnerComponent,
        MatCardModule,
        MatTabsModule,
        MatChipsModule,
        MatDividerModule
    ]
})
export class SpecialOffersListComponent implements OnInit, OnDestroy {
    @Input() destination: any;
    @Input() destination_ID: any;
    @Input() fromProperty: boolean = false;
    @Input() reviewMode: boolean = false;

    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild('offerDialog') private _offerDialog: TemplateRef<any>;

    isMobile: boolean;
    isLoading: boolean = false;
    loadingMessage: string = null;
    keywordSearchInputControl: UntypedFormControl = new UntypedFormControl("");
    typeSearchInputControl: UntypedFormControl = new UntypedFormControl("");

    destinationType: DestinationSmallType;

    elementFormGroup: UntypedFormGroup;
    specialOffersList: SpecialOffersType[] = [];

    selectedSpecialOffer: SpecialOffersType = null;
    selectedSpecialOfferForm: UntypedFormGroup;
    private _dialogRef: MatDialogRef<any>;

    languagesList: Languages[];
    offerTypesArray: any = [];
    imageURLHelper: string = null;
    flashMessage: 'success' | 'error' | 'dateChange' | null = null;

    redeemTypes = [
        {
            value: 0,
            viewValue: 'Redeem By Business Portal/App',
            description: 'Staff members must manually process and approve the redemption using the business application.'
        },
        {
            value: 1,
            viewValue: 'AutoRedeem on Code Display',
            description: 'The offer is automatically marked as redeemed the moment the user views the code on their device.'
        }
    ];

    internalCodeTypes = [
        {value: 0, viewValue: 'No Code'},
        {value: 1, viewValue: 'QR Code'},
        {value: 100, viewValue: 'Simple Label'}
    ];

    searchParameters = [
        {state: "All", value: ""},
        {state: "Active", value: 0},
        {state: "Pending", value: 1}
    ];

    private API_GET_OFFERS = "/system/offer/get";
    private API_DELETE_OFFER = "/system/offer/delete";
    private API_GET_OFFER_TYPES = "/admin/list/offertypes";
    private API_ADD_OFFER = "/system/offer/addedit";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _dialog: MatDialog,
        private route: ActivatedRoute,
        private datePipe: DatePipe,
        private _restFactoryService: RestFactoryService,
        private spinner: NgxSpinnerService,
        private breakpointObserver: BreakpointObserver,
        private _snackBar: MatSnackBar
    ) {
    }

    get isAmbassador(): boolean {
        return this.destination?.ds_destinationtype === 'Ambassador';
    }

    get termItem(): string {
        return this.isAmbassador ? 'Tour' : 'Offer';
    }

    get termItems(): string {
        return this.isAmbassador ? 'Tours' : 'Special Offers';
    }

    get termItemLower(): string {
        return this.isAmbassador ? 'tour' : 'offer';
    }

    get termItemsLower(): string {
        return this.isAmbassador ? 'tours' : 'special offers';
    }

    ngOnInit(): void {
        this.breakpointObserver.observe([
            Breakpoints.HandsetPortrait,
            Breakpoints.HandsetLandscape
        ]).subscribe(result => {
            this.isMobile = result.matches;
        });

        if (!this.destination_ID) {
            this.destination_ID = this.route.snapshot.queryParamMap.get('id_destination');
        }
        if (this.destination && !this.destination_ID) {
            this.destination_ID = this.destination.id_destination;
        }

        this.languagesList = JSON.parse(localStorage.getItem("LANGUAGES"));

        this.spinner.show().then();
        this.getApiService([
            {api: this.API_GET_OFFER_TYPES, key: "data", table: this.offerTypesArray, last: true},
        ]);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissingData(val: any, isImage: boolean = false): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.length === 0) {
                return true;
            }
            if (isImage && trimmed.endsWith('content/undefined')) {
                return true;
            }
        }
        return false;
    }

    getMissingFields(offer: SpecialOffersType): string[] {
        const missing = [];
        if (this.isMissingData(offer.ds_offer)) missing.push('Title');
        if (this.isMissingData(offer.ds_shortname)) missing.push('Short Title');
        if (this.isMissingData(offer.tx_description)) missing.push('Description');
        if (this.isMissingData(offer.im_media, true)) missing.push('Image');
        return missing;
    }

    isOfferValid(offer: SpecialOffersType): boolean {
        return this.getMissingFields(offer).length === 0;
    }

    get globalAlerts(): { message: string, type: string }[] {
        if (this.isLoading) return [];
        if (this.isAmbassador) return [];

        if (!this.specialOffersList || this.specialOffersList.length === 0) {
            return [{ message: `CRITICAL: You currently have NO ${this.termItemsLower}! You must add at least one valid active ${this.termItemLower}.`, type: 'critical' }];
        }

        let hasActive = false;
        let hasExpiringSoon = false;
        let hasUpcoming = false;

        this.specialOffersList.forEach(offer => {
            if (!this.isOfferValid(offer)) {
                return;
            }

            const status = this.getOfferDetailedStatus(offer.dt_start, offer.dt_end);
            if (status.type === 'ACTIVE') {
                hasActive = true;
            } else if (status.type === 'EXPIRING_SOON') {
                hasActive = true;
                hasExpiringSoon = true;
            } else if (status.type === 'ACTIVE_SOON' || status.type === 'SCHEDULED') {
                hasUpcoming = true;
            }
        });

        const alerts = [];

        if (!hasActive) {
            alerts.push({ message: `CRITICAL: No valid active ${this.termItemsLower}! Please ensure at least ONE ${this.termItemLower} is currently active and has all required data.`, type: 'critical' });
        }

        if (hasExpiringSoon && !hasUpcoming) {
            alerts.push({ message: `WARNING: You have an ${this.termItemLower} expiring soon, but NO valid upcoming ${this.termItemsLower} scheduled to take its place!`, type: 'warning' });
        }

        return alerts;
    }

    getApiService(apis: { api: string; key: string; table: any; last: boolean }[]): void {
        forkJoin(
            apis.map(({api, key}) =>
                this._restFactoryService.httpPostService(api, new FormData(), key)
                    .pipe(
                        takeUntil(this._unsubscribeAll),
                        catchError(() => of([]))
                    )
            )
        ).pipe(
            finalize(() => {
                this.isLoading = false;
                this.spinner.hide().then();
            })
        ).subscribe((responses) => {
            responses.forEach((response, index) => {
                apis[index].table.push(...response);
                if (apis[index].last) {
                    this.searchElements(null);
                }
            });
        });
    }

    searchElements(searcher: string, paginator = null): void {
        const data = {"id_destination": this.destination_ID};
        const payload = this._restFactoryService.createPayloadPaginator(searcher, this._paginator, data);

        this.specialOffersList = [];
        this.loadingMessage = null;
        this.isLoading = true;
        this.spinner.show().then();
        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(this.API_GET_OFFERS, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    this.spinner.hide().then();
                    this.loadingMessage = "No results found!";
                    return err;
                })
            )
            .subscribe((response) => {
                this.specialOffersList = response.ar_results;
                this.destinationType = response.destinationType;
                this._changeDetectorRef.markForCheck();
                setTimeout(() => {
                    this._paginator.length = response.vi_TotalRows;
                }, 500);
                this.spinner.hide().then();
                if (this.specialOffersList.length === 0) {
                    this.loadingMessage = `No ${this.termItemsLower} found. Add one to get started!`;
                }
            });
    }

    deleteSpecialOffer(): void {
        const dialog = this._fuseConfirmationService.open({
            title: `Delete ${this.termItem}`,
            message: `Are you sure you want to delete this ${this.termItemLower}? This action cannot be undone.`,
            actions: {confirm: {label: "Delete"},},
        });

        dialog.afterClosed().subscribe((result) => {
            if (result === "confirmed") {
                const payload = new FormData();
                payload.append("id_offer", this.selectedSpecialOffer.id_offer);
                this._restFactoryService.httpPostService(this.API_DELETE_OFFER, payload)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this._snackBar.open(`${this.termItem} deleted successfully.`, "Close", {duration: 3000});
                        this._dialogRef.close();
                        this.searchElements(null);
                    });
            }
        });
    }

    convertDateFormat(inputDate: string): string {
        if (!inputDate) return null;
        let date = new Date(inputDate);
        return this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss.SSSSSS');
    }

    saveSpecialOffer(): void {
        if (this.selectedSpecialOfferForm.invalid) {
            return;
        }
        const payload = new FormData();

        payload.append("id_language", "649a07cf-7cf8-4164-a76d-7ea2c8f6163c");
        payload.append("id_destination", this.selectedSpecialOfferForm.get("id_destination").value);
        payload.append("id_offertype", this.offerTypesArray[0].id_offertype);
        payload.append("ds_offer", this.selectedSpecialOfferForm.get("ds_offer").value);
        payload.append("ds_shortname", this.selectedSpecialOfferForm.get("ds_shortname").value);
        payload.append("tx_description", this.selectedSpecialOfferForm.get("tx_description").value);
        payload.append("dt_start", this.convertDateFormat(this.selectedSpecialOfferForm.get("dt_start").value));
        payload.append("dt_end", this.convertDateFormat(this.selectedSpecialOfferForm.get("dt_end").value));
        payload.append("vi_maxglobalredeem", this.selectedSpecialOfferForm.get("vi_maxglobalredeem").value);
        payload.append("vi_maxmemberredeem", this.selectedSpecialOfferForm.get("vi_maxmemberredeem").value);
        payload.append("en_redeemtype", this.selectedSpecialOfferForm.get("en_redeemtype").value);

        if (this.selectedSpecialOfferForm.get('en_redeemtype').value === 1) {
            payload.append("tx_redeemdata", this.selectedSpecialOfferForm.get("tx_redeemdata").value);
            payload.append("en_internalcodetype", this.selectedSpecialOfferForm.get("en_internalcodetype").value);
            payload.append("ds_internalcode", this.selectedSpecialOfferForm.get("ds_internalcode").value);
        }

        if (this.selectedSpecialOfferForm.get("id_offer").value) {
            payload.append("id_offer", this.selectedSpecialOfferForm.get("id_offer").value);
        }

        if (this.selectedSpecialOfferForm.get("im_media").value && this.selectedSpecialOfferForm.get("im_media").value !== this.selectedSpecialOffer.im_media) {
            payload.append("im_image", this.selectedSpecialOfferForm.get("im_media").value);
        }

        this._restFactoryService.httpPostService(this.API_ADD_OFFER, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                const message = this.selectedSpecialOffer.id_offer ? `${this.termItem} updated successfully.` : `${this.termItem} created successfully.`;
                this._snackBar.open(message, 'Close', {duration: 3000});
                this._dialogRef.close();
                this.searchElements(null);
            });
    }

    getTodayFormatted() {
        return new Date().toISOString();
    }

    openOfferDialog(offer: SpecialOffersType | 'new'): void {
        if (offer === 'new') {
            this.selectedSpecialOffer = {
                id_offer: null,
                id_destination: this.destination_ID,
                ds_offer: `New ${this.termItem}`,
                vi_maxmemberredeem: -1,
                vi_maxglobalredeem: -1,
                dt_start: {datetime: {date: this.getTodayFormatted()}},
                dt_end: {datetime: {date: this.getTodayFormatted()}},
                en_redeemtype: 0,
                en_internalcodetype: 0,
            } as SpecialOffersType;
        } else {
            this.selectedSpecialOffer = offer;
        }

        this.imageURLHelper = this.selectedSpecialOffer?.im_media || null;
        this.selectedSpecialOfferForm = this._formBuilder.group({
            id_offer: [this.selectedSpecialOffer.id_offer],
            id_destination: [this.selectedSpecialOffer.id_destination, Validators.required],
            dt_start: [this.convertToDate(this.selectedSpecialOffer.dt_start.datetime.date), Validators.required],
            dt_end: [this.convertToDate(this.selectedSpecialOffer.dt_end.datetime.date), Validators.required],
            ds_offer: [this.selectedSpecialOffer.ds_offer, Validators.required],
            ds_shortname: [this.selectedSpecialOffer.ds_shortname, Validators.required],
            tx_description: [this.selectedSpecialOffer.tx_description, Validators.required],
            im_media: [this.selectedSpecialOffer.im_media, this.selectedSpecialOffer.id_offer ? null : Validators.required],
            vi_maxmemberredeem: [this.selectedSpecialOffer.vi_maxmemberredeem, [Validators.required, this.numberAndMinValidator]],
            vi_maxglobalredeem: [this.selectedSpecialOffer.vi_maxglobalredeem, [Validators.required, this.numberAndMinValidator]],
            en_redeemtype: [this.selectedSpecialOffer.en_redeemtype || 0, Validators.required],
            tx_redeemdata: [this.selectedSpecialOffer.tx_redeemdata],
            en_internalcodetype: [this.selectedSpecialOffer.en_internalcodetype || 0],
            ds_internalcode: [this.selectedSpecialOffer.ds_internalcode, Validators.maxLength(50)]
        });

        this._dialogRef = this._dialog.open(this._offerDialog, {
            width: '80vw',
            maxWidth: '900px',
        });
    }

    onChangeFile(event: any): void {
        const inpF = event.target as HTMLInputElement;
        if (!inpF.files || inpF.files.length === 0) return;

        const file = inpF.files[0];
        const allowedExtensions = ["jpg", "png", "jpeg", "gif", "webp"];
        const fileExtension = file.name.toLowerCase().split('.').pop();

        if (allowedExtensions.includes(fileExtension)) {
            const urlCreator = window.URL || window.webkitURL;
            this.imageURLHelper = urlCreator.createObjectURL(file);
            this.selectedSpecialOfferForm.get('im_media').setValue(file);
            this.selectedSpecialOfferForm.get('im_media').markAsDirty();
            this._changeDetectorRef.markForCheck();
        } else {
            this._snackBar.open("Invalid image format. Please use jpg, png, jpeg, gif, or webp.", "Close", {duration: 4000});
        }
    }

    formatDate(timeDetails: TimeDetails): string {
        const date = new Date(timeDetails.datetime.date);
        return date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
    }

    getOfferDetailedStatus(start: TimeDetails, end: TimeDetails): { text: string, color: string, pulse: boolean, type: string } {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDate = new Date(start.datetime.date);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(end.datetime.date);
        endDate.setHours(0, 0, 0, 0);

        const diffDaysEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        const diffDaysStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

        if (endDate < today) {
            return {text: 'Expired', color: 'bg-gray-500 text-white', pulse: false, type: 'EXPIRED'};
        }

        if (startDate <= today && today <= endDate) {
            if (diffDaysEnd <= 7) {
                return {text: 'Expiring Soon', color: 'bg-orange-500 text-white', pulse: true, type: 'EXPIRING_SOON'};
            }
            return {text: 'Active', color: 'bg-green-600 text-white', pulse: false, type: 'ACTIVE'};
        }

        if (startDate > today) {
            if (diffDaysStart <= 7) {
                return {text: 'Active Soon', color: 'bg-blue-500 text-white', pulse: true, type: 'ACTIVE_SOON'};
            }
            return {text: 'Scheduled', color: 'bg-indigo-400 text-white', pulse: false, type: 'SCHEDULED'};
        }

        return {text: 'Unknown', color: 'bg-gray-500 text-white', pulse: false, type: 'UNKNOWN'};
    }

    getCurrentRedeemDescription(): string {
        const selectedValue = this.selectedSpecialOfferForm?.get('en_redeemtype')?.value;
        const type = this.redeemTypes.find(t => t.value === selectedValue);
        return type ? type.description : '';
    }

    get dtStartDate(): FormControl {
        return this.selectedSpecialOfferForm.get('dt_start') as FormControl;
    }

    get dtEndDate(): FormControl {
        return this.selectedSpecialOfferForm.get('dt_end') as FormControl;
    }

    onDateChange(event: MatDatepickerInputEvent<Date>): void {
        if (this.dtEndDate.value && this.dtStartDate.value > this.dtEndDate.value) {
            this.dtEndDate.setValue(this.dtStartDate.value);
            this.flashMessage = 'dateChange';
            setTimeout(() => this.flashMessage = null, 3000);
        }
    }

    makeOfferOngoing(): void {
        const startDate = this.dtStartDate.value ? new Date(this.dtStartDate.value) : new Date();
        const futureDate = new Date(startDate);
        futureDate.setFullYear(futureDate.getFullYear() + 5);

        this.dtEndDate.setValue(futureDate);
        this.dtEndDate.markAsDirty();
        this._changeDetectorRef.markForCheck();

        this._snackBar.open(`${this.termItem} duration set to 5 years from start date.`, "Close", { duration: 3000 });
    }

    convertToDate(dateString: string): Date {
        if (!dateString) return new Date();
        return new Date(dateString);
    }

    numberAndMinValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null || value === undefined || isNaN(value) || value < -1) {
            return {'numberAndMin': true};
        }
        return null;
    }

    onInputChange(event: any, controlName: string): void {
        let value = event.target.value;
        if (isNaN(value) || value === '') {
            value = -1;
        }
        if (value < -1) {
            value = -1;
        }
        this.selectedSpecialOfferForm.get(controlName).setValue(parseInt(value, 10));
    }

    onInputMaxReach(event: any, maxReach: number = 15): void {
        const inputValue = event.target.value;
        if (inputValue.length >= maxReach) {
            this._snackBar.open(`This field has a limit of ${maxReach} characters.`, "Close", {
                duration: 3000,
            });
        }
    }
}
